import React from "react";
import { Alert, AlertTitle, Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { ReportData } from "../types";
import { COLOR, fmt } from "../utils/format";
import { PARTNER_SETUP_INCOMPLETE, partnerSection } from "../utils/reportSections";

/**
 * Partner Contribution / Mortgage Share — monthly reports of projects with
 * two or more partners. If it applies but isn't set up yet, it stays on
 * screen and lists what's missing rather than disappearing.
 */
const PartnerShareTable: React.FC<{ report: ReportData }> = ({ report }) => {
  const section = partnerSection(report);
  const share = report.monthly?.partnerShare;
  if (section.state === "not_applicable" || !share) return null;

  return (
    <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
      <Box px={2} pt={1.5} pb={1}>
        <Typography variant="subtitle1" fontWeight={700}>
          Partner Contribution / Mortgage Share
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Equal ownership · Cash on hand {fmt(share.cashOnHand)} ({share.cashOnHandSource === "actual" ? "actual" : "calculated"} balance)
          {share.cashHolderName ? ` held by ${share.cashHolderName}` : ""}
        </Typography>
      </Box>

      {section.state === "incomplete" ? (
        <Alert severity="warning" sx={{ mx: 2, mb: 2 }}>
          <AlertTitle>{PARTNER_SETUP_INCOMPLETE}</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {section.missing.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </Box>
          <Typography variant="caption" display="block" mt={0.5}>
            Choose them in Month Settings below.
          </Typography>
        </Alert>
      ) : (
        <>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: COLOR.slateBg }}>
                  {section.table.headers.map((h, i) => (
                    <TableCell key={h} align={i ? "right" : "left"} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {section.table.rows.map((r) => (
                  <TableRow key={r.label} sx={r.emphasis ? { backgroundColor: COLOR.greenBg } : undefined}>
                    <TableCell sx={{ fontWeight: r.emphasis ? 700 : 400, whiteSpace: "nowrap" }}>{r.label}</TableCell>
                    {r.cells.map((c, i) => (
                      <TableCell key={i} align="right" sx={{ fontWeight: r.emphasis ? 700 : 400, whiteSpace: "nowrap" }}>
                        {c}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* The split must be impossible to miss. */}
          <Box
            sx={{
              m: 2,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: `repeat(${section.table.payments.length + 1}, 1fr)` },
              border: "2px solid #e69100",
              backgroundColor: "#fff8e1",
              borderRadius: 2,
            }}
          >
            {[
              { name: "Monthly Mortgage", amount: section.table.mortgage },
              ...section.table.payments.map((p) => ({ name: `${p.name} to Pay`, amount: p.amount })),
            ].map((cell, i) => (
              <Box key={cell.name} sx={{ textAlign: "center", py: 1.5, px: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {cell.name}
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: i === 0 ? COLOR.slateDark : "text.primary" }}>
                  {cell.amount}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Card>
  );
};

export default PartnerShareTable;
